import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { can, fmtDate } from "@/lib/helpers";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple, Key, LockKeyOpen, Trash } from "@phosphor-icons/react";

export default function Users() {
  const { user, branches } = useAuth();
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "", role_id: "", branch_id: "", phone: "", active: true });
  const [resetFor, setResetFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const canCreate = can(user, "users", "create");
  const canEdit = can(user, "users", "edit");

  const load = async () => setRows((await api.get("/users")).data);
  useEffect(() => { load(); api.get("/roles/assignable").then(({ data }) => setRoles(data)); }, []);

  const openNew = () => { setForm({ name: "", email: "", username: "", password: "", role_id: "", branch_id: "", phone: "", active: true }); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email || "", username: u.username || "", password: "", role_id: u.role_id, branch_id: u.branch_id || "", phone: u.phone || "", active: u.active }); setEditId(u.id); setOpen(true); };

  const submit = async () => {
    try {
      if (editId) {
        const payload = { name: form.name, email: form.email, username: form.username || null, role_id: form.role_id, branch_id: form.branch_id, phone: form.phone, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", { name: form.name, email: form.email, username: form.username || null, password: form.password, role_id: form.role_id, branch_id: form.branch_id, phone: form.phone });
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doReset = async () => {
    if (newPwd.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.post(`/users/${resetFor.id}/reset-password`, { new_password: newPwd }); toast.success("Password reset successfully"); setResetFor(null); setNewPwd(""); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unlock = async (u) => { try { await api.post(`/users/${u.id}/unlock`); toast.success("Account unlocked"); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggleActive = async (u) => { try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Account deactivated" : "Account activated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const removeUser = async (u) => {
    if (u.id === user.id) return toast.error("You cannot delete yourself");
    if (!window.confirm(`Delete user "${u.name}" permanently?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="User Management" subtitle="Staff accounts, roles & branch assignment.">
        {canCreate && <Button onClick={openNew} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-user-btn"><Plus size={16} className="mr-1" /> New User</Button>}
      </PageHeader>

      <Card className="bg-white border-[#E6E2D6] shadow-sm">
        {rows.length === 0 ? <EmptyState /> : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Name</TableHead><TableHead>Email / Username</TableHead><TableHead>Role</TableHead><TableHead>Type</TableHead><TableHead>Branch</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email || u.username}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email || u.username || "-"}</TableCell>
                  <TableCell><Badge className="bg-[#F2EBE1] text-[#8A6D2F] border-0">{u.role_name}</Badge></TableCell>
                  <TableCell><Badge className="bg-[#F8F6F0] text-[#4A4A4A] border border-[#E6E2D6]">{u.account_type || "Staff"}</Badge></TableCell>
                  <TableCell className="text-sm">{u.branch_name || "-"}</TableCell>
                  <TableCell>
                    {u.locked
                      ? <Badge className="bg-[#FDEDED] text-[#9D2A2A] border-0">Locked</Badge>
                      : <Badge className={u.active ? "bg-[#E8F5E9] text-[#2D6A4F] border-0" : "bg-[#FDEDED] text-[#9D2A2A] border-0"}>{u.active ? "Active" : "Disabled"}</Badge>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {canEdit && <Button variant="ghost" size="icon" onClick={() => openEdit(u)} title="Edit" data-testid={`edit-user-${u.email || u.username}`}><PencilSimple size={16} /></Button>}
                    {can(user, "users", "delete") && u.id !== user.id && <Button variant="ghost" size="icon" onClick={() => removeUser(u)} title="Delete" className="text-[#9D2A2A]" data-testid={`delete-user-${u.email || u.username}`}><Trash size={16} /></Button>}
                    {canEdit && <Button variant="ghost" size="icon" onClick={() => setResetFor(u)} title="Reset password" data-testid={`reset-user-${u.email || u.username}`}><Key size={16} /></Button>}
                    {canEdit && u.locked && <Button variant="ghost" size="icon" className="text-[#9D2A2A]" onClick={() => unlock(u)} title="Unlock account" data-testid={`unlock-user-${u.email || u.username}`}><LockKeyOpen size={16} /></Button>}
                    {canEdit && <Button variant="ghost" size="sm" className={u.active ? "text-[#9D2A2A]" : "text-[#2D6A4F]"} onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.email || u.username}`}>{u.active ? "Deactivate" : "Activate"}</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="uf-name" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="uf-email" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Username (optional, for login)</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="uf-username" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">{editId ? "Reset Password (optional)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="uf-password" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="uf-phone" /></div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Role</Label>
              <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="uf-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent className="bg-white">{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="uf-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent className="bg-white">{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editId && (
              <div className="col-span-2 flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="uf-active" />
                <Label className="text-sm">Active account</Label>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={submit} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="uf-save">{editId ? "Save User" : "Create User"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => { if (!o) { setResetFor(null); setNewPwd(""); } }}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Reset Password</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B6B6B]">Set a new password for <b>{resetFor?.name}</b> ({resetFor?.email || resetFor?.username}).</p>
          <div><Label className="text-xs text-[#6B6B6B]">New Password</Label><Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="reset-new-password" /></div>
          <DialogFooter><Button onClick={doReset} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="reset-save">Reset Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
